import React, { useState, useEffect, useCallback, memo } from "react";
import { Card, Select, DatePicker, Button, Table, Tag, message } from "antd";
import moment from "moment";
import { getAllStates, getAuditTrackingReport } from "./laundryAuditApi";

const { Option } = Select;

// Same fully-shaped-by-the-backend response as AuditRegister.js, but each
// month cell carries a simple status string instead of a percentage —
// { month_name, total_audits, audit_completed, status: 'Done'|'Pending'|'' } —
// confirmed by reading AuditReportsController.js's getAuditTrackingReport
// handler directly.
const AuditTracking = () => {
  const [states, setStates] = useState([]);
  const [stateId, setStateId] = useState(null);
  const [year, setYear] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    (async () => {
      const { error, result } = await getAllStates();
      setStates(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, []);

  const search = useCallback(async () => {
    if (!stateId) {
      message.error("Please select a state first.");
      return;
    }
    setLoading(true);
    setSearched(true);
    const { error, result } = await getAuditTrackingReport({ year: year.format("YYYY"), state_id: stateId });
    setFacilities(!error && Array.isArray(result?.result?.facilities) ? result.result.facilities : []);
    setLoading(false);
  }, [stateId, year]);

  const columns = facilities[0]
    ? [
        { title: "Facility Name", dataIndex: "facility_name", key: "facility_name", fixed: "left" },
        ...facilities[0].monthly_data.map((m, i) => ({
          title: m.month_name,
          key: `m${i}`,
          render: (_, row) => {
            const cell = row.monthly_data[i];
            if (!cell || !cell.status) return "-";
            return <Tag color={cell.status === "Done" ? "green" : "orange"}>{cell.status}</Tag>;
          },
        })),
      ]
    : [{ title: "Facility Name", dataIndex: "facility_name", key: "facility_name" }];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Year</div>
            <DatePicker
              picker="year"
              value={year}
              onChange={(d) => d && setYear(d)}
              allowClear={false}
              disabledDate={(d) => d && d > moment().endOf("year")}
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>State</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a state"
              style={{ width: 220 }}
              value={stateId}
              onChange={setStateId}
            >
              {states.map((s) => <Option key={s.state_id} value={s.state_id}>{s.state_name}</Option>)}
            </Select>
          </div>
          <Button type="primary" loading={loading} onClick={search}>Search</Button>
        </div>
      </Card>

      {searched && (
        <Card title="Audit Tracking Report">
          <Table
            rowKey="facility_id"
            columns={columns}
            dataSource={facilities}
            loading={loading}
            scroll={{ x: true }}
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: "No records for this year/state." }}
          />
        </Card>
      )}
    </div>
  );
};

export default memo(AuditTracking);
